/**
 * IndexedDB persistence for browser live-audio recording sessions.
 * Survives modal close and full page refresh.
 */

const DB_NAME = 'safemeet-live-audio';
const DB_VERSION = 1;
const STORE_SESSIONS = 'sessions';
const STORE_CHUNKS = 'chunks';
const STORE_TAKES = 'takes';

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB در این مرورگر پشتیبانی نمی‌شود.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
        db.createObjectStore(STORE_SESSIONS, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORE_CHUNKS)) {
        const chunks = db.createObjectStore(STORE_CHUNKS, { keyPath: 'id', autoIncrement: true });
        chunks.createIndex('byTake', 'takeKey', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_TAKES)) {
        const takes = db.createObjectStore(STORE_TAKES, { keyPath: 'takeKey' });
        takes.createIndex('byMeetingUser', 'meetingUserKey', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('باز کردن IndexedDB ناموفق بود.'));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('تراکنش IndexedDB لغو شد.'));
  });
}

export function sessionKey(userId, meetingId) {
  return `${userId}::${meetingId}`;
}

export function takeKey(userId, meetingId, takeId) {
  return `${userId}::${meetingId}::${takeId}`;
}

export function meetingUserKey(userId, meetingId) {
  return `${userId}::${meetingId}`;
}

export async function getActiveSession(userId, meetingId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_SESSIONS, 'readonly');
    const req = tx.objectStore(STORE_SESSIONS).get(sessionKey(userId, meetingId));
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

export async function putActiveSession(session) {
  const db = await openDb();
  const tx = db.transaction(STORE_SESSIONS, 'readwrite');
  tx.objectStore(STORE_SESSIONS).put({
    ...session,
    key: sessionKey(session.userId, session.meetingId),
  });
  await txDone(tx);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        'safemeet-live-audio-active',
        JSON.stringify({
          userId: session.userId,
          meetingId: session.meetingId,
          status: session.status,
        }),
      );
    }
  } catch {
    /* ignore */
  }
}

export async function clearActiveSession(userId, meetingId) {
  const db = await openDb();
  const tx = db.transaction(STORE_SESSIONS, 'readwrite');
  tx.objectStore(STORE_SESSIONS).delete(sessionKey(userId, meetingId));
  await txDone(tx);
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem('safemeet-live-audio-active');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.userId === userId && parsed?.meetingId === meetingId) {
          localStorage.removeItem('safemeet-live-audio-active');
        }
      }
    }
  } catch {
    /* ignore */
  }
}

export function peekActiveSessionPointer() {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('safemeet-live-audio-active');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.userId || !parsed?.meetingId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export async function appendChunk(userId, meetingId, takeId, blob) {
  const db = await openDb();
  const tx = db.transaction(STORE_CHUNKS, 'readwrite');
  tx.objectStore(STORE_CHUNKS).add({
    takeKey: takeKey(userId, meetingId, takeId),
    blob,
    createdAt: Date.now(),
  });
  await txDone(tx);
}

export async function getChunks(userId, meetingId, takeId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CHUNKS, 'readonly');
    const idx = tx.objectStore(STORE_CHUNKS).index('byTake');
    const req = idx.getAll(takeKey(userId, meetingId, takeId));
    req.onsuccess = () => {
      const rows = Array.isArray(req.result) ? req.result : [];
      rows.sort((a, b) => (a.id || 0) - (b.id || 0));
      resolve(rows.map((r) => r.blob).filter(Boolean));
    };
    req.onerror = () => reject(req.error);
  });
}

export async function clearChunks(userId, meetingId, takeId) {
  const db = await openDb();
  const key = takeKey(userId, meetingId, takeId);
  const tx = db.transaction(STORE_CHUNKS, 'readwrite');
  const store = tx.objectStore(STORE_CHUNKS);
  const idx = store.index('byTake');
  const req = idx.openCursor(IDBKeyRange.only(key));
  req.onsuccess = () => {
    const cursor = req.result;
    if (cursor) {
      cursor.delete();
      cursor.continue();
    }
  };
  await txDone(tx);
}

export async function saveCompletedTake(take) {
  const db = await openDb();
  const tx = db.transaction(STORE_TAKES, 'readwrite');
  tx.objectStore(STORE_TAKES).put({
    ...take,
    takeKey: takeKey(take.userId, take.meetingId, take.takeId),
    meetingUserKey: meetingUserKey(take.userId, take.meetingId),
  });
  await txDone(tx);
}

export async function listCompletedTakes(userId, meetingId) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_TAKES, 'readonly');
    const idx = tx.objectStore(STORE_TAKES).index('byMeetingUser');
    const req = idx.getAll(meetingUserKey(userId, meetingId));
    req.onsuccess = () => {
      const rows = Array.isArray(req.result) ? req.result : [];
      rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
}

export async function deleteCompletedTake(userId, meetingId, takeId) {
  const db = await openDb();
  const tx = db.transaction([STORE_TAKES, STORE_CHUNKS], 'readwrite');
  tx.objectStore(STORE_TAKES).delete(takeKey(userId, meetingId, takeId));
  const store = tx.objectStore(STORE_CHUNKS);
  const idx = store.index('byTake');
  const req = idx.openCursor(IDBKeyRange.only(takeKey(userId, meetingId, takeId)));
  req.onsuccess = () => {
    const cursor = req.result;
    if (cursor) {
      cursor.delete();
      cursor.continue();
    }
  };
  await txDone(tx);
}

export async function buildBlobFromChunks(userId, meetingId, takeId, mimeType) {
  const chunks = await getChunks(userId, meetingId, takeId);
  if (!chunks.length) return null;
  const type = String(mimeType || chunks[0]?.type || 'audio/webm').split(';')[0];
  const composite = new Blob(chunks, { type });
  // Copy bytes before the caller deletes the source chunks. On Safari a blob
  // made of IndexedDB chunks stays lazy; deleting the chunks makes the upload
  // stream fail and the phone aborts the request.
  try {
    if (typeof composite.arrayBuffer === 'function') {
      const bytes = await composite.arrayBuffer();
      if (bytes && bytes.byteLength > 0) return new Blob([bytes], { type });
    }
  } catch {
    /* keep the composite blob if this browser cannot copy it */
  }
  return composite;
}
